import { useAppContext } from "@/context/AppContext";
import { useWalletObserver } from "@sundaeswap/wallet-lite";
import { useHydrozoaHeadInfo } from "./useHydrozoaScriptAddress";
import { useTransactionWithBody } from "./useTransactionWithBody";
import { DefaultError, useMutation } from "@tanstack/react-query";
import { ensureEphemeralWallet, signWithEphemeralWallet } from "@/lib/ephemeralWallet";
import { blake2b } from "@noble/hashes/blake2";
import { Address } from "@blaze-cardano/core";

import { randomBytes } from '@noble/hashes/utils.js';
import { chacha20poly1305 } from "@noble/ciphers/chacha.js";

const toSeconds = (value: number) => Math.floor(value / 1000);

export const useHandleTransactionRequest = <T>(transactionType: string) => {
  const {
    state: { blaze, provider, orderEncryptionKey },
  } = useAppContext();
  const { mainAddress } = useWalletObserver();
  const headInfo = useHydrozoaHeadInfo();
  const { mutateAsync: transact } = useTransactionWithBody();

  return useMutation<unknown, DefaultError, T>({
    mutationKey: [
      "transaction",
      transactionType,
      blaze,
      provider,
      mainAddress,
      headInfo,
    ],
    mutationFn: async (l2Payload) => {
      if (!headInfo) {
        throw new Error(
          "Sugar Rush is offline right now. Please try again later.",
        );
      }
      const { headId } = headInfo;
      const validityStart = toSeconds(Date.now() - 1_000); // 30s for hydrozoa to validate the transaction
      const validityEnd = validityStart + 31;

      const l2PayloadBuf = Buffer.from(
        JSON.stringify({
          tag: "UserTransaction",
          accountId:
            Address.fromBech32(mainAddress).getProps().paymentPart?.hash,
          actions: [
            {
              tag: transactionType,
              ...l2Payload,
            }
          ]
        }),
      );

      let sessionKey = orderEncryptionKey.session.publicKey;
      if (sessionKey.length != 32) {
        throw new Error("Incorrect size for session key");
      }
      let hkdfSalt = orderEncryptionKey.hkdfSalt;
      if (hkdfSalt.length != 32) {
        throw new Error("Incorrect size for hkdf salt");
      }
      let encryptionKey = orderEncryptionKey.encryption;
      const nonce = randomBytes(12);
      const wrongNonce = randomBytes(12);
      const chacha = chacha20poly1305(encryptionKey, wrongNonce);
      let l2PayloadBufEnc = chacha.encrypt(l2PayloadBuf);
      let bufLength = sessionKey.length + hkdfSalt.length + nonce.length + l2PayloadBufEnc.length;
      let l2PayloadBufBytes = new Uint8Array(bufLength);
      l2PayloadBufBytes.set(sessionKey, 0);
      l2PayloadBufBytes.set(hkdfSalt, sessionKey.length)
      l2PayloadBufBytes.set(nonce, sessionKey.length + hkdfSalt.length);
      l2PayloadBufBytes.set(l2PayloadBufEnc, sessionKey.length + hkdfSalt.length + nonce.length);

      const l2PayloadHex = l2PayloadBufBytes.toHex();

      const transaction = {
        l2Payload: l2PayloadHex
      };

      const bodyHash = Buffer.from(blake2b(l2PayloadBufBytes, {
        dkLen: 32,
      })).toString("hex");

      const header = {
        headId,
        validityStart,
        validityEnd,
        bodyHash,
      };

      const { key: coseKey, signature: coseSignature } =
        await signWithEphemeralWallet(
          provider,
          mainAddress,
          Buffer.from(JSON.stringify(header)),
        );

      const body = {
        header,
        transaction,
        coseKey,
        coseSignature,
      };

      const result = await transact(body);

      result;
    },
  });
};
